import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolTag } from './types.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from './yield-during-work.js';

const TYPE_DEFINITION_CONTAINER_TYPES = new Set([
  'function_declaration',
  'function_signature',
  'generator_function_declaration',
  'class_declaration',
  'abstract_class_declaration',
  'interface_declaration',
  'type_alias_declaration',
  'enum_declaration',
  'module',
  'type_parameter',
  'method_definition',
  'method_signature',
  'abstract_method_signature',
  'property_signature',
  'public_field_definition',
]);

function tagDedupeKey(tag: SymbolTag): string {
  return `${tag.startLine}:${tag.startColumn}:${tag.endLine}:${tag.endColumn}:${tag.role}:${tag.kind}:${tag.name}`;
}

function isTypeDefinitionName(node: SyntaxNode): boolean {
  const parent = node.parent;
  if (!parent) return false;
  return TYPE_DEFINITION_CONTAINER_TYPES.has(parent.type) && parent.childForFieldName('name') === node;
}

function tagFromTypeIdentifierNode(node: SyntaxNode): SymbolTag {
  return {
    name: node.text,
    role: 'ref',
    kind: 'type',
    startLine: node.startPosition.row,
    startColumn: node.startPosition.column,
    endLine: node.endPosition.row,
    endColumn: node.endPosition.column,
  };
}

/** Capture type_identifier uses that tags.scm patterns miss (unions, arrays, etc.). */
export async function augmentTypeReferenceTags(
  tags: SymbolTag[],
  nameNodes: Map<string, SyntaxNode>,
  root: SyntaxNode,
): Promise<SymbolTag[]> {
  const seen = new Set(tags.map(tagDedupeKey));
  const out = [...tags];
  const state = createCodeNavYieldState();

  async function walk(node: SyntaxNode): Promise<void> {
    if (node.type === 'type_identifier' && !isTypeDefinitionName(node)) {
      const tag = tagFromTypeIdentifierNode(node);
      const key = tagDedupeKey(tag);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(tag);
        nameNodes.set(`${tag.startLine}:${tag.startColumn}:${tag.name}:${tag.role}:${tag.kind}`, node);
      }
    }
    for (const child of node.children) {
      await yieldDuringCodeNavWork(state);
      await walk(child);
    }
  }

  await walk(root);
  return out;
}
